import { useApiWithAuth } from '@/hooks/api';
import { useGroupsStore } from '../groups';
import { useEffect, useMemo, useRef, useState } from 'react';
import { loadGroups } from '../groups/actions';
import type { Person } from './types';
import type { GroupTypeKey } from '@/shared/types/group';
import { asMultiValue, asSingleValue } from '@/lib/arrays';
import { useDebounce } from '@/hooks/debounce';
import { makeSearchParam } from '@/components/people/people/utils';
import { loadMemberStatus } from '../memberstatus/actions';
import { useMemberStatusStore } from '../memberstatus';
import { apiInvoke } from '../helpers/load';
import type { PersonDocument } from '@/shared/types/pco/person';
import { personDocumentToStorePerson } from './mapFromMongo';
import type { ApiResponse } from '@/shared/types/api';

export function useMemberStatus() {
  const store = useMemberStatusStore();
  const { api, auth } = useApiWithAuth();
  const loading = useRef(false);
  const loaded = useRef(0);
  const maxAgeSeconds = 900;

  useEffect(() => {
    const now = Date.now();
    if (loading.current || loaded.current > now - maxAgeSeconds * 1000) {
      return;
    }

    loading.current = true;

    loadMemberStatus(store, { force: false, api, auth })
      .then(() => (loading.current = false))
      .catch((err) => {
        loading.current = false;
        loaded.current = now;
        console.error(err);
      });
  }, [store, auth, api]);

  return {
    lookup: store.state.lookup,
    list: store.state.list,
  };
}

export function useGroups(
  campus?: string | string[],
  type?: GroupTypeKey,
  parent?: string,
) {
  const store = useGroupsStore();
  const { api, auth } = useApiWithAuth();
  useEffect(() => {
    loadGroups(store, {
      force: false,
      api,
      auth,
      campus: asSingleValue(campus),
      campuses: asMultiValue(campus),
    });
  }, [store, auth, api, campus]);
  const list = useMemo(() => {
    let list = store.computed.activeGroupsList;
    if (campus) {
      const campuses = asMultiValue(campus, false);
      list = list.filter((item) => campuses?.includes(item.data.campus_id));
    }

    if (type) {
      list = list.filter((item) => item.data.type === type);
    }

    if (parent) {
      list = list.filter((item) => item.data.parent_id === parent);
    }

    return list;
  }, [store.computed.activeGroupsList, campus, parent, type]);
  const lookup = store.computed.groupLookup;

  return useMemo(
    () => ({
      list,
      lookup,
      // TODO parent ids and level
    }),
    [list, lookup],
  );
}

export function usePeopleLookup({
  search,
  limit = 20,
  debounce = 400,
}: {
  search: string;
  limit?: number;
  debounce?: number;
}) {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [empty, setEmpty] = useState(false);
  const input = useDebounce(search, debounce);
  const last = useRef('');
  const { api, auth } = useApiWithAuth();
  const apiRef = useRef(api);
  const authRef = useRef(auth);
  apiRef.current = api;
  authRef.current = auth;

  useEffect(() => {
    if (last.current === input) {
      return () => {};
    }

    last.current = input;
    if (!input.trim()) {
      setPeople([]);
      setEmpty(false);
      last.current = '';

      return () => {};
    }

    setEmpty(false);
    setLoading(true);
    const searchParam = makeSearchParam(input);
    if (!searchParam) {
      setPeople([]);
      setEmpty(true);
      setLoading(false);

      return () => {};
    }

    const params = new URLSearchParams({
      search: searchParam.value,
      limit: String(limit),
      searchType: searchParam.type,
    });
    const url = `/people2?${params.toString()}`;
    const currentApi = apiRef.current;
    const currentAuth = authRef.current;

    void apiInvoke<ApiResponse<PersonDocument[]>>(
      currentApi,
      url,
      currentAuth,
      {
        method: 'GET',
      },
    )
      .then((res) => {
        const items = Array.isArray(res?.data) ? res.data : [];
        const results = items.map(personDocumentToStorePerson);
        if (last.current === input) {
          setPeople(results);
          setEmpty(!results.length);
        }

        return undefined;
      })
      .catch((e) => {
        console.error(e);
        if (last.current === input) {
          setPeople([]);
          setEmpty(true);
        }
      })
      .finally(() => {
        if (last.current === input) {
          setLoading(false);
        }
      });

    return () => {
      last.current = '';
    };
  }, [limit, input]);

  return { people, loading, empty };
}
